/* R148f · Feb 2026 — Bulk Edit Tasks dialog.
 * Left column: list of selected tasks with unselect checkboxes.
 * Right column: fields to bulk-edit. Only fields the user actually
 * changes are sent — untouched fields are left as-is per task. */
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import SearchableAssigneePicker from "@/components/tasks/SearchableAssigneePicker";
import { ListChecks, X } from "lucide-react";

const STATUSES = ["open", "in_progress", "review", "done", "blocked", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const HEALTHS = [
  { v: "on_track", label: "On track" },
  { v: "at_risk", label: "At risk" },
  { v: "off_track", label: "Off track" },
];

export default function BulkEditTasksDialog({ wsId, tasks, projects = [], members = [], memberName, onClose, onApplied }) {
  const [excluded, setExcluded] = useState(new Set());
  const [patch, setPatch] = useState({});
  const [tagAdd, setTagAdd] = useState("");
  const [tagRemove, setTagRemove] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedIds = useMemo(
    () => tasks.filter((t) => !excluded.has(t.id)).map((t) => t.id),
    [tasks, excluded],
  );

  const toggle = (id) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setField = (k, v) => setPatch((p) => ({ ...p, [k]: v }));

  const apply = async () => {
    if (selectedIds.length === 0) { toast.error("No tasks selected"); return; }
    const body = { ...patch };
    if (tagAdd.trim()) body.add_tag = tagAdd.trim();
    if (tagRemove.trim()) body.remove_tag = tagRemove.trim();
    if (Object.keys(body).length === 0) { toast.error("Change at least one field before saving"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks/bulk`, {
        task_ids: selectedIds, patch: body,
      });
      const modified = data.modified ?? 0;
      const matched = data.matched ?? modified;
      if (modified > 0) {
        toast.success(`Updated ${modified} task${modified === 1 ? "" : "s"}`);
      } else if (matched === 0) {
        toast.error("No tasks matched — refresh and try again");
      } else {
        toast.message(`Already up-to-date (${matched} matched)`);
      }
      onApplied?.();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk update failed")); }
    finally { setBusy(false); }
  };

  const fieldChanged = (k) => k in patch;
  const clearField = (k) => setPatch((p) => { const c = { ...p }; delete c[k]; return c; });

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5" />
            Bulk edit tasks
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              {selectedIds.length} of {tasks.length} selected
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-[minmax(0,1fr)_1.2fr] gap-4 flex-1 overflow-hidden">
          {/* LEFT · selected tasks with uncheck */}
          <div className="border border-border overflow-y-auto" data-testid="bulk-edit-selection">
            <div className="px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold sticky top-0 z-10">
              Tasks to edit
            </div>
            <ul>
              {tasks.map((t) => {
                const on = !excluded.has(t.id);
                return (
                  <li key={t.id} className={`flex items-start gap-2 border-b border-border/60 px-3 py-2 ${on ? "" : "bg-muted/20 opacity-60"}`} data-testid={`bulk-edit-row-${t.id}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(t.id)}
                      className="mt-1"
                      data-testid={`bulk-edit-toggle-${t.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs ${on ? "font-medium" : "line-through"}`}>{t.title}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {(t.project_name || "—")} · {t.status || "open"} · {t.priority || "medium"}
                        {t.assignee_id && ` · ${memberName?.(t.assignee_id) || t.assignee_name || "assigned"}`}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* RIGHT · fields */}
          <div className="overflow-y-auto pr-1 space-y-3" data-testid="bulk-edit-fields">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Only fields you change will be updated
            </div>

            <FieldRow label="Status" changed={fieldChanged("status")} onClear={() => clearField("status")}>
              <Select value={patch.status || ""} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9" data-testid="bulk-edit-status"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="Priority" changed={fieldChanged("priority")} onClear={() => clearField("priority")}>
              <Select value={patch.priority || ""} onValueChange={(v) => setField("priority", v)}>
                <SelectTrigger className="h-9" data-testid="bulk-edit-priority"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="Assignee" changed={fieldChanged("assignee_id")} onClear={() => clearField("assignee_id")}>
              <div className="border border-border h-9 flex items-center px-2">
                <SearchableAssigneePicker
                  value={patch.assignee_id || null}
                  members={members}
                  memberName={memberName}
                  onChange={(v) => setField("assignee_id", v)}
                  placeholder="Pick teammate…"
                  size="md"
                  testid="bulk-edit-assignee"
                />
              </div>
            </FieldRow>

            <FieldRow label="Project" changed={fieldChanged("project_id")} onClear={() => clearField("project_id")}>
              <Select value={patch.project_id || ""} onValueChange={(v) => setField("project_id", v)}>
                <SelectTrigger className="h-9" data-testid="bulk-edit-project"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </FieldRow>

            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Start date" changed={fieldChanged("start_date")} onClear={() => clearField("start_date")}>
                <Input type="date" value={patch.start_date || ""} onChange={(e) => setField("start_date", e.target.value || null)} className="h-9" data-testid="bulk-edit-start" />
              </FieldRow>
              <FieldRow label="Due date" changed={fieldChanged("due_date")} onClear={() => clearField("due_date")}>
                <Input type="date" value={patch.due_date || ""} onChange={(e) => setField("due_date", e.target.value || null)} className="h-9" data-testid="bulk-edit-due" />
              </FieldRow>
            </div>

            <FieldRow label="Health" changed={fieldChanged("health")} onClear={() => clearField("health")}>
              <Select value={patch.health || ""} onValueChange={(v) => setField("health", v)}>
                <SelectTrigger className="h-9" data-testid="bulk-edit-health"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>{HEALTHS.map((h) => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}</SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="Est. effort (hours)" changed={fieldChanged("est_hours")} onClear={() => clearField("est_hours")}>
              <Input type="number" step="0.25" min="0" value={patch.est_hours ?? ""} onChange={(e) => setField("est_hours", e.target.value === "" ? null : Number(e.target.value))} className="h-9" placeholder="e.g. 4" data-testid="bulk-edit-est" />
            </FieldRow>

            <FieldRow label="Billable flags">
              <div className="flex items-center gap-3 text-sm">
                <TriToggle label="Billable" value={patch.billable} onChange={(v) => v == null ? clearField("billable") : setField("billable", v)} testid="bulk-edit-billable" />
                <TriToggle label="Billed"    value={patch.billed}   onChange={(v) => v == null ? clearField("billed") : setField("billed", v)} testid="bulk-edit-billed" />
                <TriToggle label="Paid"      value={patch.paid}     onChange={(v) => v == null ? clearField("paid") : setField("paid", v)} testid="bulk-edit-paid" />
              </div>
              {/* R159t · Feb 2026 — One-click presets that flip every
                  Billing tri-toggle to a common state transition, so
                  agencies can close out an entire month's tasks in
                  one tap without hunting through three separate
                  toggles. */}
              <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="bulk-edit-bill-presets">
                {[
                  { label: "Mark billable",   patch: { billable: true,  billed: false, paid: false } },
                  { label: "Mark billed",     patch: { billable: true,  billed: true,  paid: false } },
                  { label: "Mark paid",       patch: { billable: true,  billed: true,  paid: true  } },
                  { label: "Not billable",    patch: { billable: false, billed: false, paid: false } },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setPatch((prev) => ({ ...prev, ...preset.patch }))}
                    className="text-[11px] px-2 py-1 border border-border hover:bg-muted rounded-none"
                    data-testid={`bulk-edit-bill-preset-${preset.label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </FieldRow>

            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Add tag">
                <Input value={tagAdd} onChange={(e) => setTagAdd(e.target.value)} placeholder="e.g. urgent" className="h-9" data-testid="bulk-edit-add-tag" />
              </FieldRow>
              <FieldRow label="Remove tag">
                <Input value={tagRemove} onChange={(e) => setTagRemove(e.target.value)} placeholder="e.g. draft" className="h-9" data-testid="bulk-edit-remove-tag" />
              </FieldRow>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy} data-testid="bulk-edit-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={apply} disabled={busy || selectedIds.length === 0} data-testid="bulk-edit-apply">
            {busy ? "Saving…" : `Apply to ${selectedIds.length} task${selectedIds.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, changed, onClear, children }) {
  return (
    <div className="border border-border p-3">
      <div className="label-eyebrow text-[10px] mb-1 flex items-center gap-1">
        {label}
        {changed && (
          <button type="button" onClick={onClear} className="ml-auto text-[9px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5">
            <X className="w-2.5 h-2.5" />Clear
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function TriToggle({ label, value, onChange, testid }) {
  // undefined → don't change; true → set true; false → set false.
  const state = value === undefined ? "no_change" : value ? "true" : "false";
  return (
    <label className="inline-flex items-center gap-1.5 text-xs">
      <span>{label}</span>
      <Select value={state} onValueChange={(v) => onChange(v === "no_change" ? undefined : v === "true")}>
        <SelectTrigger className="h-8 w-24 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="no_change">No change</SelectItem>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    </label>
  );
}
